import React from "react"
import _ from "underscore"
import { CloseCircleOutlined } from "@ant-design/icons"
import { Button, Tooltip } from "antd"
import commonStrings from "../../commonStrings"
import VoiceInput from "../shared/VoiceInput"
import humbleicon from "../../../assets/images/humbleicons_ai.png"
import recording from "../../../assets/images/recording-dot.png"
import voiceinputicon from "../../../assets/images/streamline_voice-mail.png"

export default class EvidenceListItem extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      description: props.evidence.description,
      shared: props.evidence.shared,
      isRecording: false,
      isEnhancing: false,
      enhancedText: "",
      hasApplied: false,
      textAtEnhancement: "", // Track text at enhancement time
      isAiIconDisabled: false, // Track if AI icon should be disabled
      transcriptionStatus: null, // Track transcription status
    }
    this.saveEvidence = _.debounce(this.saveEvidence, 250)
    this.handleRecordingStateChange = this.handleRecordingStateChange.bind(this)
    this.handleVoiceTranscription = this.handleVoiceTranscription.bind(this)
    this.applyEnhancement = this.applyEnhancement.bind(this)
  }

  handleVoiceTranscription(transcribedText) {
    const newDescription = this.state.description
      ? `${this.state.description} ${transcribedText}`
      : transcribedText

    // Re-enable AI icon when voice input is added after enhancement
    const shouldReEnable = this.state.isAiIconDisabled
      && newDescription !== this.state.textAtEnhancement

    this.setState(
      {
        description: newDescription,
        enhancedText: "", // Clear enhanced text when voice input is added
        hasApplied: false,
        ...(shouldReEnable && { isAiIconDisabled: false }),
      },
      () => {
        this.saveEvidence()
      },
    )
  }

  handleRecordingStateChange(recording, status) {
    this.setState({
      isRecording: recording,
      ...(status && { transcriptionStatus: status }),
    })
  }

  saveEvidence() {
    showSaving()
    APIClient.put(
      `/sustainability_rubrics/${this.props.rubricId}/sustainability_rubric_indicators/${this.props.indicatorId}/evidence/${this.props.evidence.id}`,
      {
        description: this.state.description,
        shared: this.state.shared,
      },
      () => {
        hideSaving()
      },
    )
  }

  shareResponse(e) {
    this.setState({ shared: e.target.checked }, () => {
      this.saveEvidence()
    })
  }

  descriptionChanged(e) {
    const newValue = e.target.value

    // Re-enable AI icon if user adds new text after enhancement
    const shouldReEnable = this.state.isAiIconDisabled && newValue !== this.state.textAtEnhancement

    // Clear enhanced text when user manually edits
    this.setState(
      {
        description: newValue,
        enhancedText: "", // Always clear enhanced text when editing
        hasApplied: false,
        ...(shouldReEnable && { isAiIconDisabled: false }),
      },
      () => {
        this.saveEvidence()
      },
    )

    // Clear states when text is empty
    if (newValue.trim() === "") {
      this.setState({
        enhancedText: "",
        isAiIconDisabled: false,
        textAtEnhancement: "",
      })
    }
  }

  async enhanceWithAI() {
    const { description } = this.state

    if (!description || description.trim().length === 0) {
      return
    }

    this.setState({ isEnhancing: true })

    try {
      const response = await this.putAsync("/ai_enhance_text", {
        max_tokens: 200,
        temperature: 0.7,
        top_p: 1.0,
        user_message: description,
        system_prompt:
          "You are a helpful assistant that improves file descriptions to be more clear, professional, and detailed. Keep the enhanced description concise (2-3 sentences) but informative. Maintain the original meaning and key information.",
      })

      if (response.enhanced_text) {
        this.setState({
          enhancedText: response.enhanced_text,
          isEnhancing: false,
          hasApplied: false,
        })
      }
    } catch (error) {
      console.error("Error enhancing text:", error)
      this.setState({ isEnhancing: false })
    }
  }

  putAsync(path, data) {
    return new Promise((resolve, reject) => {
      APIClient.put(
        path,
        data,
        (response) => {
          resolve(response)
        },
        (error) => {
          reject(error)
        },
      )
    })
  }

  applyEnhancement() {
    this.setState(
      {
        description: this.state.enhancedText,
        textAtEnhancement: this.state.enhancedText, // Store the enhanced text
        isAiIconDisabled: true, // Disable AI icon
        enhancedText: "", // Clear the enhanced text
        hasApplied: false, // Reset hasApplied to allow future enhancements
      },
      () => {
        this.saveEvidence()
      },
    )
  }

  render() {
    const {
      evidence, disabled, deleteEvidence, color,
    } = this.props
    const {
      // eslint-disable-next-line no-unused-vars
      isRecording,
      isEnhancing,
      enhancedText,
      hasApplied,
      isAiIconDisabled,
    } = this.state

    return (
      <div className="my-4">
        <div className="mb-1 d-flex">
          <a
            style={{ color }}
            className="uploaded-links"
            href={evidence.url}
            target="_blank"
            rel="noreferrer"
          >
            {evidence.filename}
          </a>
          {!disabled && (
            <div
              className="mx-2 cursorPointer antd-logo-size-16 dflex-alignItems-center"
              onClick={() => deleteEvidence(evidence.id)}
            >
              <CloseCircleOutlined />
            </div>
          )}
        </div>

        <div className="description-input-wrapper">
          {/* Toolbar for Voice Input and AI Enhancement - Same style as Implementation Rubric */}
          {!disabled && (
            <div className="indicator-toolbar">
              <div className="indicator-toolbar-left">
                {/* Voice Recording Component */}
                {this.state.isEnhancing ? (
                  <span
                    className="indicator-toolbar-icon-btn-ai ant-tooltip-disabled-compatible-wrapper"
                    style={{
                      display: "inline-block",
                      cursor: "not-allowed",
                      width: "34px !important",
                      height: "34px !important",
                      opacity: 1,
                      borderRadius: "8px",
                    }}
                  >
                    <button
                      type="button"
                      disabled
                      className="ant-btn"
                      style={{
                        opacity: 1,
                        cursor: "not-allowed",
                        borderRadius: "8px",
                        width: "34px",
                        height: "34px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={voiceinputicon}
                        alt="Voice Input"
                        className="indicator-toolbar-icon-voice"
                      />
                    </button>
                  </span>
                ) : (
                  <VoiceInput
                    onTranscriptionComplete={this.handleVoiceTranscription}
                    onRecordingStateChange={this.handleRecordingStateChange}
                    disabled={disabled || this.state.isEnhancing}
                  />
                )}

                {/* AI Enhancement Icon */}
                <Tooltip
                  title={
                    !this.state.description
                    || this.state.description.trim() === ""
                      ? "Write something to enhance text"
                      : this.state.isRecording
                        ? "AI Enhancement Disabled During Recording"
                        : this.state.transcriptionStatus === "transcribing"
                          ? "AI Enhancement Disabled During Transcription"
                          : isEnhancing
                            ? "Generating AI Enhancement.."
                            : enhancedText && !hasApplied
                              ? "Response generated"
                              : "AI Text Enhancement"
                  }
                  placement="top"
                >
                  {isEnhancing
                  || !this.state.description
                  || this.state.description.trim() === ""
                  || (enhancedText && !hasApplied)
                  || isAiIconDisabled
                  || this.state.isRecording
                  || this.state.transcriptionStatus === "transcribing" ? (
                    <span
                      className="indicator-toolbar-icon-btn-ai ant-tooltip-disabled-compatible-wrapper"
                      style={{
                        display: "inline-block",
                        cursor: "not-allowed",
                        width: "34px !important",
                        height: "34px !important",
                        opacity: 0.6,
                        borderRadius: "8px",
                      }}
                    >
                      <button
                        type="button"
                        disabled
                        className="ant-btn"
                        style={{
                          opacity: 0.6,
                          cursor: "not-allowed",
                          borderRadius: "8px",
                          width: "34px",
                          height: "34px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {isEnhancing ? (
                          <i
                            className="fa fa-spinner fa-spin"
                            style={{ fontSize: "18px" }}
                          />
                        ) : (
                          <img
                            src={humbleicon}
                            alt="AI Enhancement"
                            className="indicator-toolbar-icon-aiIcon"
                          />
                        )}
                      </button>
                    </span>
                    ) : (
                      <Button
                        type="default"
                        onClick={() => this.enhanceWithAI()}
                        className="indicator-toolbar-icon-btn-ai"
                      >
                        {isEnhancing ? (
                          <i
                            className="fa fa-spinner fa-spin"
                            style={{ fontSize: "18px" }}
                          />
                        ) : (
                          <img
                            src={humbleicon}
                            alt="AI Enhancement"
                            className="indicator-toolbar-icon-aiIcon"
                          />
                        )}
                      </Button>
                    )}
                </Tooltip>
              </div>

              {/* Character Count */}
              <span className="indicator-character-count">
                {(this.state.description || "").length} characters
              </span>
            </div>
          )}

          <textarea
            placeholder="Please provide a description of the uploaded file..."
            value={this.state.description || ""}
            disabled={disabled}
            onChange={this.descriptionChanged.bind(this)}
            className="form-control mb-2"
          />

          {/* Recording Status - Show when actively recording (below textarea) */}
          {this.state.isRecording && (
            <div className="indicator-recording-status">
              <img
                src={recording}
                alt="Recording"
                className="indicator-recording-dot"
              />
              <span>Recording...</span>
            </div>
          )}

          {/* Enhanced Text Section */}
          {enhancedText && !hasApplied && (
            <div className="indicator-ai-response-container">
              <div className="indicator-ai-response-box">
                <div className="indicator-ai-response-header">
                  <img
                    src={humbleicon}
                    alt="AI"
                    className="indicator-ai-response-icon"
                  />
                  AI Enhanced Text
                </div>
                <div className="indicator-ai-response-text">{enhancedText}</div>
                {!hasApplied && (
                  <Button
                    onClick={this.applyEnhancement}
                    className="indicator-ai-apply-btn-enhancement-implementation"
                    disabled={this.state.isRecording || this.state.transcriptionStatus === "transcribing"}
                  >
                    Apply Enhancement
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="form-check d-flex align-items-center">
          <input
            id={`shared-${evidence.id}`}
            className="form-check-input"
            type="checkbox"
            checked={this.state.shared}
            onChange={this.shareResponse.bind(this)}
          />

          <label
            className="form-check-label small new-styling-label uploaded-links"
            htmlFor={`shared-${evidence.id}`}
          >
            {commonStrings.evidenceCheckbox}
          </label>
        </div>
      </div>
    )
  }
}
